export type DayHours = {
    open: string
    close: string
}

export type HoursGroup = {
    label: string
    hours: string
    days: Array<string>
    open: string | null
    close: string | null
}

export type PickupDay = {
    value: string
    label: string
    slots: Array<string>
}

// Opening hours by weekday, 0 is Sunday and 6 is Saturday. Use null for a day the shop is closed.
// Everything on the site (visit info, footer, pickup times, order checks) reads from here.
export const OPENING_HOURS: Record<number, DayHours | null> = {
    0: { open: '07:00', close: '15:00' },
    1: { open: '07:00', close: '15:00' },
    2: { open: '07:00', close: '15:00' },
    3: { open: '07:00', close: '15:00' },
    4: { open: '07:00', close: '15:00' },
    5: { open: '07:00', close: '15:00' },
    6: { open: '07:00', close: '15:00' }
}

export const PICKUP_INTERVAL_MINUTES = 15
export const LAST_PICKUP_BEFORE_CLOSE_MINUTES = 30
export const PICKUP_LEAD_MINUTES = 20
export const PICKUP_DAYS_AHEAD = 6

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/
const DAY_MS = 24 * 60 * 60 * 1000

const pad = (value: number): string => String(value).padStart(2, '0')

export const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)

    return hours * 60 + minutes
}

const fromMinutes = (total: number): string => `${pad(Math.floor(total / 60))}:${pad(total % 60)}`

export const isTime = (value: string): boolean => TIME.test(value)

export const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number)
    const suffix = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 === 0 ? 12 : hours % 12

    return `${displayHours}:${pad(minutes)} ${suffix}`
}

export const getPickupSlots = (weekday: number): Array<string> => {
    const hours = OPENING_HOURS[weekday]

    if (!hours) return []

    const slots: Array<string> = []
    const lastSlot = toMinutes(hours.close) - LAST_PICKUP_BEFORE_CLOSE_MINUTES

    for (let minutes = toMinutes(hours.open); minutes <= lastSlot; minutes += PICKUP_INTERVAL_MINUTES) {
        slots.push(fromMinutes(minutes))
    }

    return slots
}

export const parseIsoDate = (value: string): Date | null => {
    const match = ISO_DATE.exec(value)

    if (!match) return null

    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))

    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return null

    return date
}

export const formatIsoDate = (value: string): string => {
    const date = parseIsoDate(value)

    if (!date) return value

    return `${WEEKDAY_SHORT[date.getUTCDay()]}, ${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`
}

export const formatPickup = (date: string, time: string): string => `${formatIsoDate(date)} at ${formatTime(time)}`

export const toLocalIsoDate = (date: Date): string => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

// Pickup days and times offered in the order form, based on the visitor's own clock.
export const getPickupDays = (now: Date): Array<PickupDay> => {
    const days: Array<PickupDay> = []
    const nowMinutes = now.getHours() * 60 + now.getMinutes()

    for (let offset = 0; offset <= PICKUP_DAYS_AHEAD; offset++) {
        const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset)
        const slots = getPickupSlots(date.getDay()).filter(slot => offset > 0 || toMinutes(slot) >= nowMinutes + PICKUP_LEAD_MINUTES)

        if (slots.length === 0) continue

        const value = toLocalIsoDate(date)
        const prefix = offset === 0 ? "Today" : offset === 1 ? "Tomorrow" : null

        days.push({
            value,
            label: prefix ? `${prefix} (${formatIsoDate(value)})` : formatIsoDate(value),
            slots
        })
    }

    return days
}

// Server-side check: the time must be a real pickup slot on that weekday, and the date close to today.
// The window allows a day either side so visitors in any time zone are never refused by mistake.
export const isValidPickup = (date: string, time: string, now: Date = new Date()): boolean => {
    const pickupDate = parseIsoDate(date)

    if (!pickupDate || !isTime(time)) return false

    const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    const difference = Math.round((pickupDate.getTime() - todayUtc) / DAY_MS)

    if (difference < -1 || difference > PICKUP_DAYS_AHEAD + 1) return false

    return getPickupSlots(pickupDate.getUTCDay()).includes(time)
}

// Consecutive days that share the same hours, Monday first, for example "Monday to Friday".
export const getHoursSummary = (): Array<HoursGroup> => {
    const order = [1, 2, 3, 4, 5, 6, 0]
    const groups: Array<{ days: Array<number>, key: string }> = []

    for (const day of order) {
        const hours = OPENING_HOURS[day]
        const key = hours ? `${hours.open}-${hours.close}` : 'closed'
        const last = groups[groups.length - 1]

        if (last && last.key === key) last.days.push(day)
        else groups.push({ days: [day], key })
    }

    return groups.map(({ days }) => {
        const first = WEEKDAY_NAMES[days[0]]
        const end = WEEKDAY_NAMES[days[days.length - 1]]
        const hours = OPENING_HOURS[days[0]]
        const label = days.length === 7
            ? "Every day"
            : days.length === 1 ? first : days.length === 2 ? `${first} and ${end}` : `${first} to ${end}`

        return {
            label,
            hours: hours ? `${formatTime(hours.open)} to ${formatTime(hours.close)}` : "Closed",
            days: days.map(day => WEEKDAY_NAMES[day]),
            open: hours?.open ?? null,
            close: hours?.close ?? null
        }
    })
}

export const getFirstOpeningTime = (): string => {
    const opens = Object.values(OPENING_HOURS).filter((hours): hours is DayHours => Boolean(hours)).map(hours => hours.open)

    return opens.sort((a, b) => toMinutes(a) - toMinutes(b))[0] ?? '07:00'
}
